"use client";

/**
 * AlertCellMiniPreview — compact output peek shown INSIDE a collapsed node.
 *
 * This mounts the REAL renderers the modal uses, just clamped to ~110px tall
 * with a soft bottom fade so content visibly cuts off. The collapsed node
 * becomes a tiny window into what the step actually produced.
 *
 * Priority (highest → lowest):
 *   1. unified_search  → UnifiedSearchCarousel (real, clipped)
 *   2. image           → raw <img> (real)
 *   3. dataframe html  → DataframePreview (auto-scrolls when rows overflow)
 *   4. fund/sub agent  → AgentResultsCarousel (real, clipped)
 *   5. stdout / text   → first lines, monospace
 *   6. error           → red traceback head
 *   7. fallback        → syntax-highlighted code snippet
 *
 * Plotly / bokeh / backtest-chart / chart-html outputs are NOT shown on
 * the canvas preview at all — they only render in the expanded modal.
 * The carousel doesn't build a tab for them either (see
 * `allVisualOutputs` in AlertCellCarousel).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Prism as SyntaxHighlighterBase } from "react-syntax-highlighter";
import { materialDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { UnifiedSearchCarousel } from "@/components/notebook/UnifiedSearchCarousel";
import { AgentResultsCarousel } from "@/components/notebook/AgentResultsCarousel";
import { SignalRegistrationCard } from "@/components/notebook/SignalRegistrationCard";
import { PlotlyChart } from "@/components/notebook/PlotlyChart";
import {
  isDashboardOutput,
  isNoiseStdoutText,
} from "@/lib/alert-output-detection";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SyntaxHighlighter = SyntaxHighlighterBase as any;

/* eslint-disable @typescript-eslint/no-explicit-any */

interface RawOutput {
  type: string;
  data: any;
}

export interface AlertCellMiniPreviewProps {
  outputs?: RawOutput[];
  stdout?: string;
  code: string;
  status: "idle" | "pending" | "running" | "success" | "error";
}

// Max height the preview can grow to before a fade cuts it off. Short content
// (e.g. a single-row dataframe) sizes to its content — NO fixed void below.
//
// Keep collapsed nodes compact. The modal is where full-size charts/tables
// should breathe; the canvas preview is only a clipped teaser.
const PREVIEW_MAX = 184;
const PLOTLY_H = 170;

/**
 * Shell that lets the real renderer size itself up to `PREVIEW_MAX`.
 * No fixed height — short content hugs.
 */
function PreviewShell({
  children,
  running,
  className,
  /** If true, render inside a fixed-height "window" (for Plotly/Bokeh/Carousels
   *  whose own heights are configured via CSS override below). */
  fixed = false,
}: {
  children: React.ReactNode;
  running?: boolean;
  className?: string;
  fixed?: boolean;
}) {
  return (
    <div
      className={`relative rounded-md border border-slate-200/20 bg-slate-950/70 overflow-hidden shadow-inner ${className ?? ""}`}
      // `content-visibility: auto` + `contain-intrinsic-size` lets the
      // browser skip rendering + pause animations on offscreen previews.
      // Critical for replay perf: a 9-cell alert canvas had every
      // `df-scroll-track` / `us-scroll-track` / `code-scroll-track` /
      // `stdout-scroll-track` keyframe animation running 60fps in
      // parallel, even on cells the viewer had panned away from. With
      // these two CSS props the off-viewport cells contribute zero
      // compositor work. `contain-intrinsic-size` keeps a stable
      // placeholder height so ReactFlow node layout doesn't collapse —
      // the second number (the height) MUST match PREVIEW_MAX so the
      // node's measured height stays consistent before/after the
      // browser skips paint. The first number (width) is generous so
      // wide previews aren't clipped horizontally during the
      // transition. `auto` (vs `hidden`) means the browser still
      // honours scrollIntoView / focus / find-in-page on the content.
      style={
        fixed
          ? {
              height: PREVIEW_MAX,
              contentVisibility: "auto",
              containIntrinsicSize: `400px ${PREVIEW_MAX}px`,
            }
          : {
              maxHeight: PREVIEW_MAX,
              minHeight: 0,
              contentVisibility: "auto",
              containIntrinsicSize: `400px ${PREVIEW_MAX}px`,
            }
      }
    >
      {running && <PulseSweep />}
      <div className="pointer-events-none select-none alert-mini-preview-scope relative">
        {children}
      </div>
      <style jsx global>{`
        .alert-mini-preview-scope {
          font-size: 10.5px;
        }
        /*
         * Hard-lock pointer events on every descendant.
         *
         * The wrapper already has Tailwind's \`pointer-events-none\` but CSS
         * only hides events on that EXACT element — any descendant that
         * sets \`pointer-events: auto/all\` (Plotly does this on its
         * draglayer + tooltip layer, Bokeh does similar) claims events
         * back and shows hover tooltips even though the node is supposed
         * to be a clickable card.
         *
         * The \`!important\` override forces every descendant to ignore
         * mouse events, so clicks / hovers bubble straight up to the
         * <motion.button> and the node behaves like ONE card, not a
         * live chart widget.
         */
        .alert-mini-preview-scope,
        .alert-mini-preview-scope * {
          pointer-events: none !important;
        }
        .alert-mini-preview-scope h1,
        .alert-mini-preview-scope h2,
        .alert-mini-preview-scope h3 {
          font-size: 12px !important;
          line-height: 1.25 !important;
          margin: 0 0 2px 0 !important;
        }
        .alert-mini-preview-scope p {
          font-size: 10.5px !important;
          line-height: 1.4 !important;
          margin: 0 0 2px 0 !important;
        }
        .alert-mini-preview-scope .plotly,
        .alert-mini-preview-scope .js-plotly-plot {
          height: ${PLOTLY_H}px !important;
          min-height: ${PLOTLY_H}px !important;
        }
        /* Hide Plotly's modebar inside the mini preview — no reason to
         * show zoom/pan/screenshot icons on a non-interactive thumbnail.
         * The modebar appears on hover even when pointer-events are off
         * because Plotly keeps it in the DOM; \`display: none\` kills it
         * visually too. */
        .alert-mini-preview-scope .modebar-container,
        .alert-mini-preview-scope .hovertext,
        .alert-mini-preview-scope .hoverlayer {
          display: none !important;
        }
        /*
         * Hide Plotly's internal figure title + subtitle inside the node
         * preview. The card's own header ALREADY shows the cell's purpose
         * at the top of every node — re-rendering the same title inside
         * the plotly figure just eats 60-100px of chart area, which is
         * why wide heatmaps / treemaps were rendering with only the
         * top-left cell visible (e.g. "Crypto Top 50 · 24h Heatmap"
         * with only BTC showing and the rest cropped off).
         *
         * The modal still renders the full title because there's plenty
         * of vertical space there. This override is scoped to the
         * mini-preview ONLY via .alert-mini-preview-scope.
         *
         * - .g-gtitle       → Plotly's title SVG group
         * - .annotation-text-g with _subtitle_ anchor → Plotly subtitle
         *   (added when layout.title.subtitle is set)
         */
        .alert-mini-preview-scope .g-gtitle,
        .alert-mini-preview-scope .infolayer .g-gtitle {
          display: none !important;
        }
      `}</style>
    </div>
  );
}

export function AlertCellMiniPreview({
  outputs,
  stdout,
  code,
  status,
}: AlertCellMiniPreviewProps) {
  const safe = useMemo<RawOutput[]>(
    () => (Array.isArray(outputs) ? outputs : []),
    [outputs],
  );

  const trimmedCode = useMemo(() => {
    if (!code) return "# (empty)";
    const nonEmpty = code
      .split("\n")
      .filter((l) => l.trim().length > 0)
      .slice(0, 5)
      .join("\n");
    return nonEmpty || "# (empty)";
  }, [code]);

  // Plotly is the PREFERRED visual when available. PKA emits plotly
  // alongside a PNG fallback in the same display_data bundle; we want
  // the interactive chart, not the static snapshot. Locked by user
  // feedback: "always choose plotly".
  const plotlyOutput = useMemo(
    () => safe.find((o) => o.type === "plotly"),
    [safe],
  );

  // Detect the first image output — used ONLY when no plotly exists in
  // the same cell. When plotly and image coexist, plotly wins and the
  // image is silently dropped from the preview.
  const imageOutput = useMemo(
    () => safe.find((o) => o.type === "image"),
    [safe],
  );

  const unifiedSearch = useMemo(
    () => safe.filter((o) => o.type === "unified_search").map((o) => o.data),
    [safe],
  );

  const signalRegister = useMemo(
    () => safe.filter((o) => o.type === "signal_register").map((o) => o.data),
    [safe],
  );

  const agentResults = useMemo(() => {
    const arr: Array<{ type: "fund_agent" | "sub_agent"; data: any }> = [];
    for (const o of safe) {
      if (o.type === "fund_agent") arr.push({ type: "fund_agent", data: o.data });
      else if (o.type === "sub_agent")
        arr.push({ type: "sub_agent", data: o.data });
    }
    return arr;
  }, [safe]);

  const dataHtml = useMemo(() => {
    return safe.find(
      (o) =>
        o.type === "html" &&
        typeof o.data === "string" &&
        /<table/i.test(o.data) &&
        !isDashboardOutput(o) &&
        !(/data-visualization-type="backtest-tabs"/i.test(o.data)),
    );
  }, [safe]);

  const hasFinishedSuccessfully = status === "success" && safe.length > 0;
  const isRunningOrPending =
    status === "running" || status === "pending" || status === "idle";

  // ── Priority 0: signal_register (the hero card on a signal canvas) ──
  if (hasFinishedSuccessfully && signalRegister.length > 0) {
    return <SignalRegisterPreview data={signalRegister[0]} />;
  }

  // ── Priority 1: unified_search (REAL carousel, auto-scrolls when overflow) ──
  if (hasFinishedSuccessfully && unifiedSearch.length > 0) {
    return <UnifiedSearchPreview results={unifiedSearch} />;
  }

  // ── Priority 2a: plotly — REAL interactive chart, always preferred ─
  //
  // User rule: "always choose plotly". PKA emits the same chart in two
  // forms inside one display_data bundle — a plotly JSON payload (the
  // real vector chart) and an `image/png` URL (a static snapshot of
  // the same chart). We want the real plotly in the node preview so
  // the user sees axes/data clearly at card size. The PlotlyChart
  // component is the same one the modal uses; the global mini-preview
  // scope CSS in <PreviewShell> already clamps .js-plotly-plot height
  // to PLOTLY_H (150px), hides the modebar, and kills pointer events.
  if (hasFinishedSuccessfully && plotlyOutput) {
    const data = plotlyOutput.data;
    if (data && typeof data === "object") {
      return (
        <PreviewShell>
          <PlotlyChart data={data as any} noBorder />
        </PreviewShell>
      );
    }
  }

  // ── Priority 2b: image — only when NO plotly exists on this cell ───
  //
  // Images fill the node's width and let height follow the natural
  // aspect ratio (no fixed height cap). Used for cells that produced
  // a bare PNG without an accompanying plotly payload — rare, usually
  // direct matplotlib savefig output.
  if (hasFinishedSuccessfully && imageOutput && !plotlyOutput) {
    const src = typeof imageOutput.data === "string" ? imageOutput.data : "";
    if (src) {
      return (
        <div className="relative rounded-md border border-slate-200/20 bg-slate-950/70 overflow-hidden shadow-inner pointer-events-none select-none">
          <img
            src={src}
            alt="Output"
            className="block w-full h-auto"
          />
        </div>
      );
    }
  }

  // ── Priority 3: dataframe-ish html — auto-scrolls when rows overflow ───
  if (hasFinishedSuccessfully && dataHtml) {
    return <DataframePreview html={String(dataHtml.data)} />;
  }

  // ── Priority 4: fund/sub agent carousel (REAL, hugs content) ─────────
  if (hasFinishedSuccessfully && agentResults.length > 0) {
    return (
      <PreviewShell>
        <div style={{ zoom: 0.82 }}>
          <AgentResultsCarousel results={agentResults as any} />
        </div>
      </PreviewShell>
    );
  }

  // ── Priority 5: errors — real traceback, hugs content, bottom fade ──
  if (safe.length > 0) {
    const err = safe.find((o) => o.type === "error");
    if (err) {
      const msg =
        typeof err.data === "string"
          ? err.data
          : err.data?.evalue ||
            err.data?.ename ||
            JSON.stringify(err.data || {});
      return (
        <div
          className="relative rounded-md border border-red-400/40 bg-red-500/[0.08] overflow-hidden"
          style={{ maxHeight: PREVIEW_MAX, minHeight: 0 }}
        >
          <div className="px-3 py-2 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-red-300 shrink-0 mt-0.5" />
            <pre className="font-mono text-[10.5px] leading-[1.55] text-red-50 whitespace-pre-wrap break-words">
              {String(msg).slice(0, 400)}
            </pre>
          </div>
        </div>
      );
    }
  }

  // ── Priority 6: stdout / text ──────────────────────────────────
  if (hasFinishedSuccessfully) {
    // Skip Plotly placeholder text outputs so the mini preview never
    // shows "[Plotly Figure]" as if it were real stdout.
    const textOut = safe.find(
      (o) =>
        o.type === "text" &&
        typeof o.data === "string" &&
        !isNoiseStdoutText(o.data),
    );
    const textData = textOut?.data || stdout;
    if (typeof textData === "string" && textData.trim().length > 0) {
      const lines = textData
        .split("\n")
        .map((l) => l.trimEnd())
        .filter((l) => l.trim().length > 0 && !isNoiseStdoutText(l))
        .slice(0, 6);
      if (lines.length > 0) {
        return (
          <PreviewShell>
            <pre className="px-3 py-2 font-mono text-[10.5px] leading-[1.55] text-slate-100 whitespace-pre">
              {lines.join("\n")}
            </pre>
          </PreviewShell>
        );
      }
    }
  }

  // ── Fallback: VSCode-styled code preview (shown while running / idle) ──
  return (
    <PreviewShell running={isRunningOrPending}>
      <SyntaxHighlighter
        language="python"
        style={materialDark}
        PreTag="pre"
        customStyle={{
          margin: 0,
          padding: "6px 10px",
          background: "transparent",
          fontSize: "10.5px",
          lineHeight: "1.55",
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        }}
        codeTagProps={{
          style: {
            whiteSpace: "pre",
            overflow: "hidden",
            display: "block",
          },
        }}
      >
        {trimmedCode}
      </SyntaxHighlighter>
    </PreviewShell>
  );
}

/**
 * DataframePreview — the canvas mini-preview for an `html` output that
 * contains a `<table>` (pandas `.to_html()` is the canonical producer).
 *
 * Why this exists: the raw pandas table easily overflows the 184px shell
 * (20+ rows is normal). Previously the content was hard-clipped at the
 * bottom — you could only ever see the top 5–6 rows and everything else
 * was invisible on the canvas. Now we detect overflow and start a slow
 * linear downward auto-scroll (15 px/sec, same rate as the Code tab),
 * mirroring the teleprompter trick: double the content and translate
 * by -50% so the loop lands frame-perfect on the start of the second
 * copy with no visible jump.
 */
function DataframePreview({ html }: { html: string }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [durationSec, setDurationSec] = useState(0);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const track = trackRef.current;
    if (!wrapper || !track || typeof ResizeObserver === "undefined") return;

    const compute = () => {
      // Track holds TWO copies of the table — its scrollHeight is
      // effectively 2× the single-table height. We want to know the
      // single-copy height to decide "overflows?" and to compute the
      // scroll duration.
      const singleHeight = track.scrollHeight / 2;
      const shellHeight = wrapper.clientHeight;
      const overflow = singleHeight - shellHeight;
      // 15 px/sec — same rate as CodeView so the two animations feel
      // native together. Minimum 10s so very short overflows don't
      // whiplash.
      const RATE = 15;
      const seconds = Math.max(10, Math.floor(singleHeight / RATE));
      setShouldScroll(overflow > 20);
      setDurationSec(seconds);
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(wrapper);
    ro.observe(track);
    // Tables can reflow as fonts / CSS load in the iframe-less mini
    // preview — re-measure a couple of times to catch late layout.
    const timers = [400, 1200, 3000].map((ms) =>
      window.setTimeout(compute, ms),
    );

    return () => {
      ro.disconnect();
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [html]);

  return (
    <div
      ref={wrapperRef}
      className="relative rounded-md border border-slate-200/20 bg-slate-950/70 overflow-hidden shadow-inner pointer-events-none select-none"
      // content-visibility: auto pauses the dfScroll keyframe + skips
      // paint when the cell is panned offscreen on the canvas. See the
      // PreviewShell wrapper above for the full rationale; same
      // contain-intrinsic-size pattern keeps ReactFlow node layout
      // stable across the on-screen ↔ off-screen transition.
      style={{
        maxHeight: PREVIEW_MAX,
        minHeight: 0,
        contentVisibility: "auto",
        containIntrinsicSize: `400px ${PREVIEW_MAX}px`,
      }}
    >
      <div
        ref={trackRef}
        className={`alert-mini-df ${shouldScroll ? "df-scroll-track" : ""}`}
      >
        <div
          className="px-2.5 py-2 text-[10px] leading-[1.35] text-slate-100"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: html }}
        />
        {shouldScroll && (
          // Second copy so the translateY -50% loop is seamless.
          <div
            className="px-2.5 py-2 text-[10px] leading-[1.35] text-slate-100"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: html }}
            aria-hidden
          />
        )}
      </div>
      {shouldScroll && (
        <div
          aria-hidden
          className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-b from-transparent to-slate-950/95"
        />
      )}
      <style jsx>{`
        .alert-mini-df :global(table) {
          border-collapse: collapse;
          width: 100%;
        }
        .alert-mini-df :global(th),
        .alert-mini-df :global(td) {
          border: 1px solid rgba(148, 163, 184, 0.22);
          padding: 3px 6px;
          text-align: left;
          white-space: nowrap;
        }
        .alert-mini-df :global(th) {
          background: rgba(186, 230, 253, 0.1);
          color: rgb(224 242 254);
          font-weight: 600;
        }
        .alert-mini-df :global(tr:nth-child(even) td) {
          background: rgba(148, 163, 184, 0.04);
        }
        .df-scroll-track {
          animation: dfScroll ${durationSec}s linear infinite;
          will-change: transform;
        }
        @keyframes dfScroll {
          from {
            transform: translateY(0);
          }
          to {
            transform: translateY(-50%);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .df-scroll-track {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}

/**
 * SignalRegisterPreview — canvas mini-preview for `signal_register` outputs.
 * Mounts the real SignalRegistrationCard, clamped to the preview shell with a
 * soft bottom fade (matching the other previews) so the collapsed node teases
 * what fired this run.
 */
function SignalRegisterPreview({ data }: { data: any }) {
  return (
    <div
      className="relative overflow-hidden"
      style={{ maxHeight: PREVIEW_MAX }}
    >
      <SignalRegistrationCard data={data} />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-10"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0) 0%, var(--background) 100%)",
        }}
      />
    </div>
  );
}

/**
 * UnifiedSearchPreview — the canvas mini-preview for `unified_search`
 * outputs (Twitter / News / Reddit cards).
 *
 * Why this exists: the full card list easily overflows the 184px shell
 * (each source card is ~150-180px tall, the typical search step returns
 * 5-10 of them). Previously the content was hard-clipped at the bottom
 * and only the first card was ever visible on the canvas. Now we detect
 * overflow and start a slow linear upward auto-scroll (15 px/sec, same
 * rate as Code / Stdout / Dataframe), mirroring the teleprompter trick:
 * double the content and translate by -50% so the loop lands
 * frame-perfect on the start of the second copy with no visible jump.
 */
function UnifiedSearchPreview({ results }: { results: any[] }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [durationSec, setDurationSec] = useState(0);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const track = trackRef.current;
    if (!wrapper || !track || typeof ResizeObserver === "undefined") return;

    const compute = () => {
      // Track holds TWO copies of the carousel — its scrollHeight is
      // effectively 2x the single-copy height. We want the single-copy
      // height to decide "overflows?" and to compute the scroll duration.
      const singleHeight = track.scrollHeight / 2;
      const shellHeight = wrapper.clientHeight;
      const overflow = singleHeight - shellHeight;
      // 30 px/sec — deliberately faster than the 15 px/sec used by
      // CodeView / StdoutView / DataframePreview. Search cards are tall
      // (~120-150px each at zoom 0.82) so at the slow rate a single card
      // took 8-10s to scroll past, which felt frozen. Doubling the rate
      // keeps the cadence lively while still being readable in passing.
      // Minimum 6s so very short overflows don't whiplash.
      const RATE = 30;
      const seconds = Math.max(6, Math.floor(singleHeight / RATE));
      setShouldScroll(overflow > 20);
      setDurationSec(seconds);
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(wrapper);
    ro.observe(track);
    // Card avatars, icon fonts, and Tailwind classes can settle slightly
    // after first paint — re-measure a few times to catch late layout.
    const timers = [400, 1200, 3000].map((ms) =>
      window.setTimeout(compute, ms),
    );

    return () => {
      ro.disconnect();
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [results]);

  return (
    <div
      ref={wrapperRef}
      className="relative rounded-md border border-slate-200/20 bg-slate-950/70 overflow-hidden shadow-inner pointer-events-none select-none alert-mini-preview-scope"
      // Pauses usScroll keyframe when offscreen — see PreviewShell.
      style={{
        maxHeight: PREVIEW_MAX,
        minHeight: 0,
        contentVisibility: "auto",
        containIntrinsicSize: `400px ${PREVIEW_MAX}px`,
      }}
    >
      <div
        ref={trackRef}
        className={`alert-mini-unified-search ${shouldScroll ? "us-scroll-track" : ""}`}
      >
        <div style={{ zoom: 0.82 }}>
          <UnifiedSearchCarousel results={results as any} />
        </div>
        {shouldScroll && (
          // Second copy so the translateY -50% loop is seamless.
          <div style={{ zoom: 0.82 }} aria-hidden>
            <UnifiedSearchCarousel results={results as any} />
          </div>
        )}
      </div>
      {shouldScroll && (
        <div
          aria-hidden
          className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-b from-transparent to-slate-950/95"
        />
      )}
      <style jsx>{`
        .us-scroll-track {
          animation: usScroll ${durationSec}s linear infinite;
          will-change: transform;
        }
        @keyframes usScroll {
          from {
            transform: translateY(0);
          }
          to {
            transform: translateY(-50%);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .us-scroll-track {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}

/** Icy sweep while a step is executing. Signals "live". */
function PulseSweep() {
  return (
    <div
      className="pointer-events-none absolute inset-0 z-10"
      style={{
        background:
          "linear-gradient(90deg, transparent 0%, rgba(186,230,253,0.14) 50%, transparent 100%)",
        backgroundSize: "200% 100%",
        animation: "alertPulseSweep 1.8s ease-in-out infinite",
      }}
    >
      <style jsx>{`
        @keyframes alertPulseSweep {
          0% {
            background-position: 120% 0;
          }
          100% {
            background-position: -120% 0;
          }
        }
      `}</style>
    </div>
  );
}
